import { useState } from 'react'
import styled from 'styled-components'
import { Button, Grid, Wrapper } from '@farewill/ui'
import { FONT, GTR } from '@farewill/ui/tokens'
import { useDispatch, useSelector } from 'react-redux'
import { useQueryClient } from 'react-query'
import { Formik, Form, Field, useField } from 'formik'
import * as yup from 'yup'
import Dinero from 'dinero.js'

import { hideModal } from 'state/actions'
import { RESOURCES } from 'api'
import useApi from 'lib/effects/api'
import {
  formatCurrency,
  hasDecimals,
  fromDecimal,
} from 'lib/formatting/currency'
import FakeInput from 'components/fake-input'
import Label from 'components/styled/label'
import { input } from 'components/styled/helpers/form'
import Error from 'components/form/error'
import { formatVoucherCodeOptions } from 'routes/lead/partners-and-discounts/formatVoucherCodeOptions'
import PRICING from 'config/pricing'

import type { ProbateCase } from 'lib/models/probate-case'
import { processDiscount, VoucherType } from 'lib/models/voucher'
import { SelectInput } from 'components/form'
import { LeadVoucher } from 'lib/models/lead'

// Mirrors server GRANT_ONLY_MEMBERSHIP_VOUCHER_CAP_IN_PENCE
const GRANT_ONLY_MEMBERSHIP_VOUCHER_CAP_IN_PENCE = 50_00

const createValidationSchema = ({
  statingPrice,
  startingVoucher,
}: {
  statingPrice: string
  startingVoucher: string
}) =>
  yup.object().shape({
    newServiceFee: yup
      .string()
      .required('Please enter a new price')
      .matches(/^[0-9.]+$/, 'Please enter a valid price')
      .test('is-valid-number', 'Please enter a valid price', (value) => {
        if (!value) return false
        const num = parseFloat(value)
        return !isNaN(num) && num >= 0
      })
      .test(
        'has-price-or-voucher-change',
        'Please change either the service fee or the voucher',
        function (value) {
          const { voucherType } = this.parent
          const feeChanged = value !== statingPrice
          const voucherChanged = voucherType !== startingVoucher
          return feeChanged || voucherChanged
        }
      ),
    reason: yup.string().required('Please enter a reason for the change'),
  })

const StyledButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${GTR.S};
`

const StyledCancelButton = styled(Button)`
  font-weight: ${FONT.WEIGHT.REGULAR};
  text-decoration: underline;
  padding: ${GTR.S} ${GTR.M};

  &:hover {
    text-decoration: underline;
  }
`

const StyledTextarea = styled(Field)`
  ${input}
  resize: none;
  min-height: 150px;
`

const StyledInput = styled(Field)`
  ${input}
`

type EditPriceModalConfig = {
  currentPriceInPence: number
  availableVouchers?: LeadVoucher[]
  currentVoucher?: string
}

type EditPriceModalProps = {
  config: EditPriceModalConfig
}

const TextareaField = ({ name, label }: { name: string; label: string }) => {
  const [field, meta] = useField(name)
  const hasError = Boolean(meta.error && meta.touched)

  return (
    <Wrapper>
      <Label $error={hasError} htmlFor={name}>
        {label}
      </Label>
      <StyledTextarea
        {...field}
        id={name}
        name={name}
        component="textarea"
        $error={hasError}
      />
      <Error name={name} />
    </Wrapper>
  )
}

const CurrencyInputField = ({
  name,
  label,
}: {
  name: string
  label: string
}) => {
  const [field, meta] = useField(name)
  const hasError = Boolean(meta.error && meta.touched)

  return (
    <Wrapper>
      <Label $error={hasError} htmlFor={name}>
        {label}
      </Label>
      <StyledInput
        {...field}
        id={name}
        name={name}
        type="text"
        $error={hasError}
      />
      <Error name={name} />
    </Wrapper>
  )
}

const getVoucherDiscountAmountInPence = ({
  priceIncVatInPence,
  voucher,
  isGrantOnly,
}: {
  priceIncVatInPence: number
  voucher: LeadVoucher
  isGrantOnly: boolean
}): number => {
  if (
    isGrantOnly &&
    voucher.mechanism === 'membership' &&
    voucher.discountType === VoucherType.valueInPence
  ) {
    return Math.min(
      GRANT_ONLY_MEMBERSHIP_VOUCHER_CAP_IN_PENCE,
      voucher.discountValue
    )
  }

  return processDiscount({
    priceInPence: priceIncVatInPence,
    discountType: voucher.discountType,
    discountValue: voucher.discountValue,
  }).discountAmountInPence
}

const getFinalPriceInPence = ({
  newServiceFee,
  voucher,
  isGrantOnly,
}: {
  newServiceFee: string
  voucher?: LeadVoucher
  isGrantOnly: boolean
}): number | null => {
  if (newServiceFee === '') return null

  const price = parseFloat(newServiceFee)
  const priceExclVatInPence = fromDecimal(price).getAmount()

  // Discount is applied after VAT (same order as the quote calculator / API).
  const priceIncVatInPence = Math.round(
    priceExclVatInPence * (1 + PRICING.VAT_RATE)
  )

  if (!voucher) return priceIncVatInPence

  const discountAmountInPence = getVoucherDiscountAmountInPence({
    priceIncVatInPence,
    voucher,
    isGrantOnly,
  })

  return priceIncVatInPence - discountAmountInPence
}

export const EditPriceModal = ({ config }: EditPriceModalProps) => {
  const dispatch = useDispatch()
  const cache = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const probateCase = useSelector(
    (state: { probateCase: ProbateCase }) => state.probateCase
  )

  const [, makeRequest] = useApi()

  const { currentPriceInPence, availableVouchers = [], currentVoucher } = config

  const formatPrice = (valueInPence: number) => {
    const formatted = formatCurrency({
      value: valueInPence,
      valueInPence: true,
      showPence: hasDecimals(valueInPence / 100),
    })
    // Remove the £ symbol as it's shown in the label
    return formatted.replace('£', '')
  }

  let startingVoucher = 'none'
  if (currentVoucher) {
    startingVoucher =
      availableVouchers.find((voucher) => voucher.code === currentVoucher)
        ?.code || 'none'
  }
  const statingPrice = Dinero({ amount: currentPriceInPence }).toFormat('0.00')
  const initialValues = {
    newServiceFee: statingPrice,
    voucherType: startingVoucher,
    reason: '',
  }

  const voucherTypeSelectOptions = [
    { label: 'No voucher', value: 'none' },
    ...formatVoucherCodeOptions({ voucherCodes: availableVouchers }),
  ]

  const findVoucherByCode = (code: string) =>
    availableVouchers.find((voucher) => voucher.code === code)

  const handleSubmit = async (values: typeof initialValues) => {
    setIsSubmitting(true)

    try {
      const priceAsDinero = fromDecimal(parseFloat(values.newServiceFee))
      const priceInPence = priceAsDinero.getAmount()
      await makeRequest({
        url: `/api/probate-cases/${probateCase.id}/quote`,
        method: 'POST',
        data: {
          data: {
            type: 'probate_case_quotes',
            attributes: {
              price: priceInPence,
              reason: values.reason,
              voucherCode:
                values.voucherType === 'none' ? undefined : values.voucherType,
            },
          },
        },
      })

      cache.invalidateQueries([
        RESOURCES.INVOICES,
        `filter[probateCaseId]=${probateCase.id}`,
      ])
      cache.invalidateQueries([RESOURCES.PROBATE_QUOTE_HISTORY, probateCase.id])

      dispatch(hideModal())
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    dispatch(hideModal())
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createValidationSchema({
        statingPrice,
        startingVoucher,
      })}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        const finalPriceInPence = getFinalPriceInPence({
          newServiceFee: values.newServiceFee,
          voucher: findVoucherByCode(values.voucherType),
          isGrantOnly: !probateCase.attributes.estateAdministrationRequired,
        })

        return (
          <Form>
            <Grid>
              <Grid.Item span={6}>
                <FakeInput
                  label="Service fee (excl. VAT)"
                  value={formatPrice(currentPriceInPence)}
                />
              </Grid.Item>
              <Grid.Item span={6}>
                <CurrencyInputField
                  name="newServiceFee"
                  label="New service fee (excl. VAT)"
                />
              </Grid.Item>
              <Grid.Item span={6}>
                <SelectInput
                  label="Voucher"
                  name="voucherType"
                  options={voucherTypeSelectOptions}
                />
              </Grid.Item>
              <Grid.Item span={6}>
                <FakeInput
                  label="New service fee (incl. VAT and Discounts)"
                  value={
                    finalPriceInPence === null
                      ? '-'
                      : formatPrice(finalPriceInPence)
                  }
                />
              </Grid.Item>
              <Grid.Item>
                <TextareaField name="reason" label="Reason for change" />
              </Grid.Item>
            </Grid>

            <StyledButtonRow>
              <StyledCancelButton type="button" onClick={handleCancel}>
                Cancel
              </StyledCancelButton>
              <Button.Primary type="submit" loading={isSubmitting}>
                Save changes
              </Button.Primary>
            </StyledButtonRow>
          </Form>
        )
      }}
    </Formik>
  )
}

export default EditPriceModal
